import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import type { TimezoneDTO } from '../types/dto/timezone.dto';
import { logger } from 'src/lib/observability/logger';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const UTC_TIMEZONE = {
  id: 'UTC',
  name: 'UTC',
  offset: 0,
  dstOffset: 0,
};

export const getUTCText = (timezone?: TimezoneDTO) => {
  const { offset, dstOffset } = timezone ?? UTC_TIMEZONE;
  const totalOffsetSeconds = offset + (dstOffset ?? 0);
  const totalOffsetHours = totalOffsetSeconds / 3600;
  return `UTC ${totalOffsetHours > 0 ? '+' : ''}${totalOffsetHours}`;
};


export const parseInTimezone = (
  date: Date | string | dayjs.Dayjs,
  timezoneId?: string,
  preserveTime = false,
) => dayjs.tz(
    dayjs(date).format(preserveTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'),
    timezoneId ?? 'UTC',
  );

export const convertToTimezone = (
  date: Date | string | dayjs.Dayjs,
  targetTimezone: string,
) => dayjs(date).tz(targetTimezone ?? 'UTC');

/**
 * Formats an instant in the given IANA zone: wall time, numeric offset, and zone id
 * (offset matches that moment, including DST).
 */
export function formatAppointmentInstant(iso: string | Date, timeZoneId?: string): string {
  const tz = timeZoneId?.trim() || 'UTC';
  const local = convertToTimezone(iso, tz);
  if (!local.isValid()) {
    return String(iso);
  }
  return `${local.format('DD MMM YYYY HH:mm:ss')} (${local.format('Z')}) · ${tz}`;
}

/** Calendar date in the given zone only — no clock time (for compact tooltips). */
export function formatAppointmentDateInZone(iso: string | Date, timeZoneId?: string): string {
  const tz = timeZoneId?.trim() || 'UTC';
  const local = convertToTimezone(iso, tz);
  if (!local.isValid()) {
    return String(iso);
  }
  return `${local.format('DD MMM YYYY')} · ${tz}`;
}

/** Compact calendar date in the given zone (activities grid Scheduled column). */
export function formatAppointmentShortDateInZone(iso: string | Date, timeZoneId?: string): string {
  const tz = timeZoneId?.trim() || 'UTC';
  const local = convertToTimezone(iso, tz);
  if (!local.isValid()) {
    return '';
  }
  return local.format('DD MMM YY');
}

/**
 * Returns a display string for a timezone: "Asia/Kolkata (UTC+5:30)" or "UTC".
 * Uses the current offset (respects DST).
 */
export function getTimezoneDisplayLabel(timezoneId: string | undefined): string {
  const tz = timezoneId?.trim() || 'UTC';
  if (tz === 'UTC') return 'UTC';
  try {
    const offset = dayjs().tz(tz).format('Z'); // e.g. "+05:30", "-01:00", "+00:00"
    if (offset === '+00:00' || offset === 'Z') return `${tz} (UTC)`;
    const sign = offset.startsWith('-') ? '-' : '+';
    const [hours, minutes] = offset.replace(/[+-]/, '').split(':').map(Number);
    const absHours = hours ?? 0;
    const absMinutes = minutes ?? 0;
    const offsetStr =
      absMinutes === 0 ? `UTC${sign}${absHours}` : `UTC${sign}${absHours}:${String(absMinutes).padStart(2, '0')}`;
    return `${tz} (${offsetStr})`;
  } catch (err) {
    logger.error('Invalid timezone ID in getTimezoneDisplayLabel', { timezoneId: tz, error: err }, true);
    return tz;
  }
}